import { useAtomValue } from "jotai";
import type { FunctionComponentWithRef } from "react";
import { useCallback, useMemo } from "react";
import { FollowType } from "scmp-sdk";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { Mynews } from "shared/data/tracking/module";
import { useResponsive, useTrackImpressionEventByIntersection } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { MyNewsLink } from "scmp-app/components/home/my-news-daily-five/my-news-link";
import { RecommendArticles } from "scmp-app/components/home/my-news-daily-five/recommend-articles";
import { SetupCallToAction } from "scmp-app/components/home/my-news-daily-five/setup-call-to-action";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { useMyNews } from "scmp-app/lib/hooks";
import { userServiceAtom } from "scmp-app/lib/user-service/atoms";

import MyNewsLogo from "./myNewsLogo.svg";
import { useMyNewDailyFiveArticleIds } from "./recommend-articles/hooks";
import {
  Container,
  ContentContainer,
  DownloadContainer,
  LinksContainer,
  LogoBaseLink,
  LogoContainer,
  StyledBaseLink,
  SubTitle,
  Title,
  TitleContainer,
} from "./styles";

type Props = unknown;

export const MyNewsDailyFive: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  ref: forwardReference,
}) => {
  const isDesktop = useResponsive(theme.breakpoints.up("desktop"), false);

  const downloadLink = isDesktop
    ? "https://app.scmp.com/"
    : "https://scmp.onelink.me/3586748601/43f04bbb";

  const { follows = [] } = useAtomValue(userServiceAtom);
  const { initialHasEnoughFollowedItems } = useMyNews();

  const followTypeMapping = useMemo(
    () =>
      follows
        .map(follow => follow.split(":"))
        .reduce(
          (mapping, [type, entityId]) => ({
            ...mapping,
            [type]: [...mapping[type as FollowType], entityId],
          }),
          {
            [FollowType.Author]: [] as string[],
            [FollowType.Section]: [] as string[],
            [FollowType.Topic]: [] as string[],
          },
        ),
    [follows],
  );

  const { recommendedArticleIds, recommendedId } = useMyNewDailyFiveArticleIds({
    authorIds: followTypeMapping[FollowType.Author],
    sectionIds: followTypeMapping[FollowType.Section],
    topicIds: followTypeMapping[FollowType.Topic],
  });

  const listOfArticlesIds = recommendedArticleIds?.join(",") ?? "";

  const ga4ImpressionEvent = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticlesIds,
        page_type: "homepage",
        recommendation_id: recommendedId,
        widget_name: "mynews",
      },
      subcategory: "widget",
    }),
    [listOfArticlesIds, recommendedId],
  );

  const handleMyNewsLogoClick = useCallback(() => {
    // 10.1.1
    sendGA4Tracking({
      action: "click",
      category: "mynews",
      customized_parameters: {
        action_type: "redirect",
        trigger_point: "mynews_widget",
      },
      subcategory: "mydaily",
    });
  }, []);

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: ga4ImpressionEvent,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const setReference = (element: HTMLDivElement) => {
    if (typeof forwardReference === "function") {
      forwardReference(element);
    } else if (forwardReference) {
      (forwardReference as React.MutableRefObject<HTMLDivElement>).current = element;
    }
    captureTrackImpressionEventTargetElement(element);
  };

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.MyDaily5,
      }}
    >
      <Container ref={setReference}>
        <LogoContainer>
          <LogoBaseLink
            as="/mynews"
            onClick={handleMyNewsLogoClick}
            pathname="/mynews"
            query={{ module: Mynews }}
          >
            <MyNewsLogo />
          </LogoBaseLink>
        </LogoContainer>
        <TitleContainer>
          <Title>My Daily 5</Title>
          <SubTitle>Five daily stories, curated for you</SubTitle>
        </TitleContainer>
        <ContentContainer>
          {initialHasEnoughFollowedItems ? (
            <RecommendArticles recommendedArticleIds={recommendedArticleIds} />
          ) : (
            <SetupCallToAction />
          )}
        </ContentContainer>
        <LinksContainer>
          {initialHasEnoughFollowedItems && (
            <MyNewsLink onClick={() => {}}>more stories you might like</MyNewsLink>
          )}
          <DownloadContainer>
            <StyledBaseLink pathname={downloadLink}>Download SCMP app</StyledBaseLink>
            <span> for the best experience</span>
          </DownloadContainer>
        </LinksContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

MyNewsDailyFive.displayName = "MyNewsDailyFive";
